import { useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../config/api";

export default function ForgotPasswordPage() {
  const [tenantSlug, setTenantSlug] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  async function submit() {
    if (!email.trim()) return setError("Please enter your email address.");
    setError("");
    setLoading(true);
    try {
      const resetBaseUrl = window.location.origin + "/reset-password";
      const res = await apiFetch("/api/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({
          tenantSlug: tenantSlug.trim() || null,
          email: email.trim(),
          resetBaseUrl,
        }),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Something went wrong.");
      }
      setSent(true);
    } catch (e) {
      setError(e?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.root}>
      <div style={styles.dotGrid} />
      <div style={styles.glowTop} />

      <div style={styles.card}>
        <div style={styles.logoRow}>
          <img src="/logo.svg" alt="Tackl" style={{ width: 32, height: 32, objectFit: "contain" }} />
          <div>
            <div style={styles.wordmark}>Tackl</div>
            <div style={styles.wordmarkSub}>TACKL</div>
          </div>
        </div>

        {sent ? (
          <>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 10 }}>Check your inbox</div>
            <p style={{ fontSize: 14, color: "#697386", lineHeight: 1.6 }}>
              If <strong style={{ color: "#111827" }}>{email}</strong> is registered, we've sent a password reset link. Check your email and follow the link to reset your password.
            </p>
            <p style={{ fontSize: 13, color: "#697386", marginTop: 12 }}>
              Didn't receive it? Check your spam folder, or{" "}
              <button onClick={() => setSent(false)} style={styles.linkBtn}>try again</button>.
            </p>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 24 }}>
              <h1 style={styles.heading}>Forgot your password?</h1>
              <p style={styles.subheading}>Enter your work email and we'll send you a reset link.</p>
            </div>

            {error && <div style={styles.errorBox}>{error}</div>}

            <div style={styles.form}>
              <div style={styles.field}>
                <label style={styles.label}>WORKSPACE</label>
                <input
                  type="text"
                  placeholder="default"
                  value={tenantSlug}
                  onChange={e => setTenantSlug(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && submit()}
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>EMAIL</label>
                <input
                  type="email"
                  placeholder="you@tackl.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && submit()}
                  style={styles.input}
                />
              </div>

              <button
                onClick={submit}
                disabled={loading}
                style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1, cursor: loading ? "not-allowed" : "pointer" }}
              >
                {loading ? "Sending…" : "Send Reset Link →"}
              </button>
            </div>
          </>
        )}

        <div style={{ marginTop: 24, textAlign: "center" }}>
          <Link to="/login" style={{ fontSize: 13, color: "#0b72b9", textDecoration: "none", fontWeight: 600 }}>
            ← Back to Sign In
          </Link>
        </div>
      </div>

      <p style={styles.tagline}>Tackl</p>
    </div>
  );
}

const styles = {
  root: {
    minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", background: "#f5f6f9", position: "relative",
    overflow: "hidden", padding: "40px 20px",
    fontFamily: "'Segoe UI', 'Plus Jakarta Sans', system-ui, sans-serif",
  },
  dotGrid: {
    position: "absolute", inset: 0,
    backgroundImage: "radial-gradient(circle, rgba(17,24,39,0.045) 1px, transparent 1px)",
    backgroundSize: "28px 28px", pointerEvents: "none",
  },
  glowTop: {
    display: "none",
    position: "absolute", top: "-10%", left: "50%", transform: "translateX(-50%)",
    width: 600, height: 400, borderRadius: "50%",
    background: "radial-gradient(ellipse, rgba(8,145,178,0.18) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  card: {
    position: "relative", width: "100%", maxWidth: 420,
    background: "#fff", border: "1px solid #dfe3ea",
    borderRadius: 12, padding: "34px 34px 30px",
    boxShadow: "0 12px 36px rgba(16,24,40,0.10)",
  },
  logoRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 28 },
  wordmark: { fontSize: 16, fontWeight: 700, color: "#111827", letterSpacing: 0, lineHeight: 1.2 },
  wordmarkSub: { fontSize: 10, fontWeight: 600, color: "#8a94a6", letterSpacing: "0.12em", marginTop: 2 },
  heading: { fontSize: 22, fontWeight: 700, color: "#111827", letterSpacing: 0, lineHeight: 1.25, marginBottom: 6 },
  subheading: { fontSize: 13, color: "#697386", lineHeight: 1.5 },
  errorBox: {
    padding: "10px 14px", borderRadius: 10, background: "var(--danger-soft)",
    border: "1px solid var(--danger-border)", color: "#991b1b", fontSize: 13, marginBottom: 14,
  },
  form: { display: "flex", flexDirection: "column", gap: 14 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", color: "#697386" },
  input: {
    background: "#f8fafc", border: "1px solid #d5dbe6",
    color: "#111827", borderRadius: 8, padding: "11px 14px", fontSize: 14,
    outline: "none", width: "100%", fontFamily: "'Segoe UI', 'Plus Jakarta Sans', system-ui, sans-serif",
  },
  submitBtn: {
    marginTop: 4, width: "100%", padding: "12px", fontSize: 14, fontWeight: 600,
    background: "#0b72b9", color: "white",
    border: "none", borderRadius: 8, boxShadow: "0 3px 10px rgba(11,114,185,0.22)",
    fontFamily: "'Segoe UI', 'Plus Jakarta Sans', system-ui, sans-serif",
  },
  linkBtn: {
    background: "none", border: "none", color: "#0b72b9", cursor: "pointer",
    textDecoration: "underline", fontSize: 13, padding: 0,
  },
  tagline: { marginTop: 28, fontSize: 12, color: "#8a94a6", letterSpacing: "0.02em" },
};
